import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import LoadDetailModal from '@/components/client/LoadDetailModal';
import { Package, Plus, MapPin, ArrowRight, Eye, Wifi, WifiOff, BarChart3, TrendingUp, DollarSign, Search, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import MapView from '@/components/MapView';
import ShipmentWizard from '@/components/ShipmentWizard';
import { useFreightSocket } from '@/hooks/useFreightSocket';
import { getLoadStatusClasses } from '@/lib/statusColors';

const milesToKm = (miles) => Math.round((miles || 0) * 1.60934);

export default function ClientDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const location = useLocation();
  const routeTab = useMemo(() => {
    if (location.pathname.includes('shipments')) return 'active';
    if (location.pathname.includes('new-shipment')) return 'new';
    if (location.pathname.includes('history')) return 'history';
    return 'overview';
  }, [location.pathname]);
  const [activeTab, setActiveTab] = useState(routeTab);
  useEffect(() => { setActiveTab(routeTab); }, [routeTab]);
  const [trackingLoad, setTrackingLoad] = useState(null);
  const [detailLoadId, setDetailLoadId] = useState(null);
  const [trackingData, setTrackingData] = useState(null);
  const [driverLocations, setDriverLocations] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const { lastEvent, connected } = useFreightSocket(token, user);

  const fetchAll = useCallback(() => {
    Promise.all([
      api.get('/loads'),
      api.get('/client/analytics'),
      api.get('/tracking/latest'),
    ]).then(([loadsRes, analyticsRes, locRes]) => {
      setLoads(loadsRes.data);
      setAnalytics(analyticsRes.data);
      const locs = {};
      for (const loc of locRes.data) locs[loc.driver_id] = { lat: loc.lat, lng: loc.lng };
      setDriverLocations(locs);
    }).catch(console.error).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (lastEvent?.type === 'load_update') {
      fetchAll();
      toast.info('Load status updated');
    }
    if (lastEvent?.type === 'location_update') {
      setDriverLocations(prev => ({ ...prev, [lastEvent.driver_id]: { lat: lastEvent.lat, lng: lastEvent.lng } }));
    }
  }, [lastEvent, fetchAll]);

  const activeLoads = loads.filter(l => !['completed', 'invoiced', 'draft'].includes(l.status));
  const completedLoads = loads.filter(l => ['completed', 'invoiced'].includes(l.status));

  // Filtered loads for history tab
  const filteredLoads = loads.filter(l => {
    if (statusFilter !== 'all' && l.status !== statusFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!((l.cargo_description || '').toLowerCase().includes(q) ||
            (l.load_id || '').toLowerCase().includes(q) ||
            (l.pickup_address || '').toLowerCase().includes(q) ||
            (l.delivery_address || '').toLowerCase().includes(q))) return false;
    }
    return true;
  });

  const handleTrack = async (load) => {
    setTrackingLoad(load);
    try {
      const res = await api.get(`/tracking/${load.load_id}`);
      setTrackingData(res.data);
    } catch { toast.error('Failed to load tracking'); }
  };

  // Map markers for active loads
  const mapMarkers = [];
  const mapLoads = [];
  activeLoads.forEach(l => {
    mapMarkers.push({ id: `p-${l.load_id}`, lat: l.pickup_lat, lng: l.pickup_lng, type: 'pickup', label: l.pickup_name || 'Pickup' });
    mapMarkers.push({ id: `d-${l.load_id}`, lat: l.delivery_lat, lng: l.delivery_lng, type: 'delivery', label: l.delivery_name || 'Delivery' });
    if (l.driver_id && driverLocations[l.driver_id]) {
      mapMarkers.push({ id: `t-${l.driver_id}`, lat: driverLocations[l.driver_id].lat, lng: driverLocations[l.driver_id].lng, type: 'truck', label: 'Driver' });
      mapLoads.push({ ...l, driver_lat: driverLocations[l.driver_id].lat, driver_lng: driverLocations[l.driver_id].lng });
    } else { mapLoads.push(l); }
  });

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="client-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Shipper Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Welcome back, {user?.company_name || user?.name}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${connected ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-destructive/10 text-destructive border-destructive/20'}`}>
            {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {connected ? 'Live' : 'Offline'}
          </div>
          <Button onClick={() => navigate('/client/new-shipment')} className="bg-brand text-brand-foreground hover:bg-brand/90">
            <Plus className="w-4 h-4 mr-2" /> New Shipment
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">

        {/* Analytics Tab */}
        <TabsContent value="overview" className="space-y-4">
          {analytics && (
            <>
              <div className="grid grid-cols-4 gap-4">
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold">{analytics.total_loads}</p><p className="text-xs text-muted-foreground mt-1">Total Loads</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{analytics.active_loads}</p><p className="text-xs text-muted-foreground mt-1">Active</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">${analytics.total_spent?.toLocaleString()}</p><p className="text-xs text-muted-foreground mt-1">Total Spent</p>
                </CardContent></Card>
                <Card><CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold">${analytics.avg_cost_per_km}</p><p className="text-xs text-muted-foreground mt-1">Avg $/km</p>
                </CardContent></Card>
              </div>

              {/* Status Distribution */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader><CardTitle className="text-sm">Status Breakdown</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {Object.entries(analytics.status_distribution || {}).map(([status, count]) => (
                        <div key={status} className="flex justify-between items-center">
                          <Badge className={getLoadStatusClasses(status)}>{status.replace(/_/g, ' ')}</Badge>
                          <span className="font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader><CardTitle className="text-sm">Top Routes</CardTitle></CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(analytics.top_routes || []).slice(0, 5).map((r, i) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground truncate max-w-[200px]">{r.route}</span>
                          <span className="font-medium">{r.count} loads</span>
                        </div>
                      ))}
                      {(!analytics.top_routes || analytics.top_routes.length === 0) && <p className="text-muted-foreground text-sm">No data yet</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Monthly Trend */}
              {analytics.monthly_trend?.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-sm">Monthly Trend</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                      {analytics.monthly_trend.map(m => (
                        <div key={m.month} className="min-w-[120px] bg-muted/50 rounded-lg p-3 text-center">
                          <p className="text-xs text-muted-foreground">{m.month}</p>
                          <p className="text-lg font-bold mt-1">{m.loads} loads</p>
                          <p className="text-xs text-emerald-600 font-medium">${m.spent?.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* Active/Dispatched Tab */}
        <TabsContent value="active" className="space-y-4">
          {activeLoads.length > 0 && (
            <div className="rounded-xl overflow-hidden border" style={{ height: 350 }}>
              <MapView markers={mapMarkers} loads={mapLoads} dark={false} showSatelliteToggle showLegend zoom={5} />
            </div>
          )}
          <div className="space-y-3">
            {activeLoads.map(l => (
              <Card key={l.load_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setDetailLoadId(l.load_id)}>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getLoadStatusClasses(l.status)}>{l.status.replace(/_/g, ' ')}</Badge>
                        <span className="text-xs text-muted-foreground">{l.load_id}</span>
                      </div>
                      <p className="font-medium">{l.cargo_description}</p>
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3 text-emerald-500" />
                        <span className="truncate max-w-[150px]">{(l.pickup_name || l.pickup_address || '').split(',')[0]}</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="truncate max-w-[150px]">{(l.delivery_name || l.delivery_address || '').split(',')[0]}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">${l.rate?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{milesToKm(l.distance_miles)} km</p>
                      {l.driver_id && (
                        <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => handleTrack(l)}>
                          <Eye className="w-3 h-3 mr-1" /> Track
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Contact info */}
                  {(l.shipper_contact_name || l.receiver_contact_name) && (
                    <div className="flex gap-4 mt-3 pt-3 border-t text-xs text-muted-foreground">
                      {l.shipper_contact_name && <span>Shipper: {l.shipper_contact_name} {l.shipper_contact_phone && `· ${l.shipper_contact_phone}`}</span>}
                      {l.receiver_contact_name && <span>Receiver: {l.receiver_contact_name} {l.receiver_contact_phone && `· ${l.receiver_contact_phone}`}</span>}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            {activeLoads.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No active shipments</p>
                <Button className="mt-4" onClick={() => setWizardOpen(true)}><Plus className="w-4 h-4 mr-2" /> Create Shipment</Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* New Load Tab */}
        <TabsContent value="new">
          <Card>
            <CardContent className="pt-6">
              <ShipmentWizard token={token} user={user} onClose={() => { setActiveTab('active'); fetchAll(); }} embedded />
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Input placeholder="Search loads by ID, cargo, address..." value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)} className="h-9" />
            </div>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 text-sm">
              <option value="all">All Status</option>
              {['draft','posted','assigned','at_pickup','in_transit','at_delivery','completed','invoiced'].map(s => (
                <option key={s} value={s}>{s.replace(/_/g,' ')}</option>
              ))}
            </select>
          </div>

          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Load ID</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Distance</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLoads.map(l => (
                    <TableRow key={l.load_id} className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailLoadId(l.load_id)}>
                      <TableCell className="font-mono text-xs">{l.load_id.slice(-8)}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{l.cargo_description}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {(l.pickup_address || '').split(',')[0]} → {(l.delivery_address || '').split(',')[0]}
                      </TableCell>
                      <TableCell className="font-medium">${l.rate?.toLocaleString()}</TableCell>
                      <TableCell>{milesToKm(l.distance_miles)} km</TableCell>
                      <TableCell><Badge className={getLoadStatusClasses(l.status)}>{l.status.replace(/_/g,' ')}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{l.created_at?.slice(0, 10)}</TableCell>
                    </TableRow>
                  ))}
                  {filteredLoads.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No loads found</TableCell></TableRow>}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Shipment Wizard Dialog */}
      {wizardOpen && (
        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
            <ShipmentWizard token={token} user={user} onClose={() => { setWizardOpen(false); setActiveTab('active'); fetchAll(); }} />
          </DialogContent>
        </Dialog>
      )}

      {/* Uber-Style Tracking Dialog */}
      {trackingLoad && (
        <Dialog open={!!trackingLoad} onOpenChange={() => { setTrackingLoad(null); setTrackingData(null); }}>
          <DialogContent className="bg-card border-border text-foreground max-w-2xl p-0 overflow-hidden">
            {/* Map — large, fills top */}
            <div className="relative" style={{ height: 350 }}>
              <MapView markers={[
                { id: 'p', lat: trackingLoad.pickup_lat, lng: trackingLoad.pickup_lng, type: 'pickup', label: trackingLoad.pickup_name || 'Pickup' },
                { id: 'd', lat: trackingLoad.delivery_lat, lng: trackingLoad.delivery_lng, type: 'delivery', label: trackingLoad.delivery_name || 'Delivery' },
                ...(trackingLoad.driver_id && driverLocations[trackingLoad.driver_id] ? [
                  { id: 't', lat: driverLocations[trackingLoad.driver_id].lat, lng: driverLocations[trackingLoad.driver_id].lng, type: 'truck', label: 'Driver', color: '#E5A100' }
                ] : []),
              ]} loads={[{
                ...trackingLoad,
                driver_lat: trackingLoad.driver_id && driverLocations[trackingLoad.driver_id] ? driverLocations[trackingLoad.driver_id].lat : null,
                driver_lng: trackingLoad.driver_id && driverLocations[trackingLoad.driver_id] ? driverLocations[trackingLoad.driver_id].lng : null,
              }]} />
              {/* ETA overlay */}
              <div className="absolute top-3 left-3 bg-card/95 backdrop-blur rounded-xl px-4 py-2 shadow-lg">
                <p className="text-xs text-muted-foreground">Estimated arrival</p>
                <p className="text-xl font-bold text-primary">
                  {trackingLoad.distance_miles ? `${Math.round(trackingLoad.distance_miles * 1.60934 / 80 * 60)} min` : '—'}
                </p>
                <p className="text-xs text-muted-foreground">{milesToKm(trackingLoad.distance_miles)} km total</p>
              </div>
              {/* Status badge overlay */}
              <div className="absolute top-3 right-3">
                <Badge className={`${getLoadStatusClasses(trackingLoad.status)} shadow-lg text-xs`}>{trackingLoad.status.replace(/_/g,' ').toUpperCase()}</Badge>
              </div>
            </div>

            {/* Bottom info panel */}
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-lg">{trackingLoad.cargo_description}</h3>
                <span className="text-primary font-bold text-lg">${trackingLoad.rate?.toLocaleString()}</span>
              </div>

              {/* Route visualization */}
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center gap-1 mt-1">
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <div className="w-0.5 h-8 bg-border" />
                  <div className="w-3 h-3 rounded-full bg-black" />
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-sm font-medium">{trackingLoad.pickup_name || (trackingLoad.pickup_address || '').split(',')[0]}</p>
                    <p className="text-xs text-muted-foreground">{trackingLoad.pickup_address}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">{trackingLoad.delivery_name || (trackingLoad.delivery_address || '').split(',')[0]}</p>
                    <p className="text-xs text-muted-foreground">{trackingLoad.delivery_address}</p>
                  </div>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-4 gap-3 pt-2 border-t">
                <div className="text-center">
                  <p className="text-lg font-bold">{milesToKm(trackingLoad.distance_miles)}</p>
                  <p className="text-[10px] text-muted-foreground">km</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold">{trackingLoad.weight_lbs?.toLocaleString()}</p>
                  <p className="text-[10px] text-muted-foreground">lbs</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold">{trackingLoad.box_count || trackingLoad.pallet_count || '—'}</p>
                  <p className="text-[10px] text-muted-foreground">{trackingLoad.pallet_count ? 'pallets' : 'boxes'}</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold text-primary">{trackingData?.locations?.length || 0}</p>
                  <p className="text-[10px] text-muted-foreground">GPS pts</p>
                </div>
              </div>

              {/* Contact info */}
              {(trackingLoad.receiver_contact_name || trackingLoad.shipper_contact_name) && (
                <div className="bg-muted/30 rounded-lg p-3 text-sm space-y-1">
                  {trackingLoad.shipper_contact_name && (
                    <p className="text-muted-foreground">Shipper: <span className="text-foreground font-medium">{trackingLoad.shipper_contact_name}</span> {trackingLoad.shipper_contact_phone && `· ${trackingLoad.shipper_contact_phone}`}</p>
                  )}
                  {trackingLoad.receiver_contact_name && (
                    <p className="text-muted-foreground">Receiver: <span className="text-foreground font-medium">{trackingLoad.receiver_contact_name}</span> {trackingLoad.receiver_contact_phone && `· ${trackingLoad.receiver_contact_phone}`}</p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Load Detail Modal */}
      <LoadDetailModal
        open={!!detailLoadId}
        onOpenChange={(v) => { if (!v) setDetailLoadId(null); }}
        loadId={detailLoadId}
      />
    </div>
  );
}
